import { AxiosError, AxiosResponse } from 'axios';
import environment from '@environment';
import { CacheAreaKey } from 'core/config/cache-config';
import { EndpointHelper, UrlBuilder } from 'core/services/endpoint.helper';
import { HttpClient } from 'core/services/http';
import { StorageHelper } from 'core/services/storage';
import { CurrencyStore } from 'core/stores/currency.store';
import { SiteConfigStore } from 'core/stores/site-config.store';

type GetRecurringCustomerRequest = {
  customerNumber: string;
  country: string;
  channel: string;
  businessUnit: string;
}

type CancelRecurringCustomerRequest = GetRecurringCustomerRequest;

type GetRecurringCustomerResponse = {
  lastName: string;
  registrationStatus: string;
  businessUnit: string;
  dueDate: string;
  emailId: string;
  maskedCardNumber: string;
  cardExpiryDate: string;
  createdAt: string;
  actionStatus: string;
  smartCardNumber: string;
  cardHolderName: string;
  firstName: string;
  phoneNumber: string;
}

// type SetupRecurringPaymentResponse = {
//   success: boolean;
//   responseMessage: string;
//   token: string;
//   transactionId: string;
//   redirectUrl: string;
// }

//Yes, this is in PascalCase unlike other responses ::facepalm::
//but they seemed to have changed the case after it was pointed out, without
//telling anyone
type StandardApiResponse = {
  StatusCode?: number;
  statusCode?: number;
  Message: string;
  ReferenceId: string;
}

const useRequestCache = <T>(areaKey: CacheAreaKey, url: string) => {
  const storage = (StorageHelper<T>).GetInstance;

  return {
    getFromCache: () => {
      return storage.getFromCache('req', url, areaKey)
    },
    setCacheAndReturn: <T>(result: AxiosResponse<T>) => {
      const response = result?.data as any;

      return storage.setCacheAndReturn('req', areaKey, response, url) as T;
    }
  }
}

//TODO: Merge with DPPaymentService
export class PaymentApi {
  private apiBuilder = new EndpointHelper(`${environment.UTILITY_API_URI}/uni-api/apipaymentservice/api/v1`);

  constructor(private _customerNumber: string, private _country: string) {
  }

  private _getUrl(path: string) {
    return this.apiBuilder.createUrl(path).build();
  }

  private _getCoreDetails() {
    return {
      customerNumber: this._customerNumber,
      businessUnit: SiteConfigStore.businessUnit(),
      channel: SiteConfigStore.channel(),
      country: this._country,
    }
  }

  cancelRecurringPayment() {
    const url = this._getUrl('Payment/CancelRecurringCustomer');

    const request: CancelRecurringCustomerRequest = this._getCoreDetails();

    return HttpClient.GetInstance
      .post<CancelRecurringCustomerRequest, StandardApiResponse>(url, request)
      .then(response => {
        const data = response?.data;
        const statusCode = data?.StatusCode ?? data?.statusCode;
        return statusCode === 200;
      })
      .catch(error => {
        console.error('Error cancelling recurring payment', error);
        return false;
      });
  }

  getRecurringCustomer(clearCache = false) {
    const url = this._getUrl('Payment/GetRecurringCustomer');

    const request: GetRecurringCustomerRequest = this._getCoreDetails();

    const {
      getFromCache,
      setCacheAndReturn
    } = useRequestCache<GetRecurringCustomerResponse>(CacheAreaKey.Payments, url);

    const cachedResponse = !clearCache ? getFromCache() : undefined;

    if (cachedResponse) {
      return Promise.resolve(cachedResponse);
    }

    return HttpClient.GetInstance
      .post<GetRecurringCustomerRequest, GetRecurringCustomerResponse>(url, request)
      .then(setCacheAndReturn)
      .catch(error => {
        const axiosError = error as AxiosError;

        if (!axiosError) {
          return Promise.reject(error);
        }

        const response = axiosError.response;

        switch (response?.status) {
          case 404:
            console.info(response?.data);
            return undefined;
        }

        return Promise.reject(response?.data);
      });
  }

  getTokenizedPaymentUrl(currencyStore: CurrencyStore, amount: number) {
    //This is for ET & ZM only

    return new UrlBuilder(environment.MAH.Urls.RecurringPayment, '')
      .params
      .addCountry(this._country)
      .addCustomerNumber(this._customerNumber)
      .addChannel(SiteConfigStore.channel())
      .add('amount', amount)
      .add('currency', currencyStore.mainCurrency.code)
      .build();

    //https://qa-api.dstvafrica.com/apipaymentservice/Payments/SetUpRecurring?country=Ethiopia&amount=21&currency=USD&Channel=webuni
  }
}